<template>
  <ul
    class="frisbuy-customer-review__media-list"
    :style="{
      flexWrap: props.wrap,
      gap: props.gap,
    }"
  >
    <li
      v-for="(media, idx) in props.mediaList"
      :key="idx"
      class="frisbuy-customer-review__media-list-item"
      :style="{
        backgroundImage: `url(${DomainSubstitute(
          media.thumbnailUrl
        )}), url(${errorImagePlug})`,
        width: props.mediaWidth,
        height: props.mediaHeight,
        aspectRatio: props.mediaAspectRatio,
      }"
      @click="() => selectMedia(media)"
    >
      <!-- NOTE: now image displaying as "background-image" for show `errorImagePlug` if image cannot be load -->
      <img
        class="frisbuy-customer-review__media-image"
        :src="DomainSubstitute(media.thumbnailUrl)"
        @error="errorImageHandler"
      />

      <span
        v-if="media.videoUrl.length > 0"
        class="frisbuy-customer-review__play-icon"
      >
        <svg width="29" height="31" viewBox="0 0 29 31" fill="none">
          <g filter="url(#filter0_d_4369_3101)">
            <path
              d="M21.4186 12.7112C23.5271 13.9506 23.5271 17.0494 21.4186 18.2888L10.7442 24.5638C8.63566 25.8032 6 24.2539 6 21.7749L6 9.22509C6 6.74611 8.63566 5.19675 10.7442 6.43624L21.4186 12.7112Z"
              fill="white"
            />
          </g>
          <defs>
            <filter
              id="filter0_d_4369_3101"
              x="-3"
              y="-2"
              width="35"
              height="35"
              filterUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB"
            >
              <feFlood flood-opacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset />
              <feGaussianBlur stdDeviation="3" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.36 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_4369_3101"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect1_dropShadow_4369_3101"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </span>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.frisbuy-customer-review__media-list {
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  display: flex;
  flex-direction: row;
  overflow-x: auto;
  overflow-y: hidden;

  list-style: none;

  padding: 0;
  margin: 0;

  .frisbuy-customer-review__media-list-item {
    display: inline-block;
    position: relative;

    flex: 0 0 auto;

    background-size: cover;
    background-position: center;

    cursor: pointer;

    .frisbuy-customer-review__media-image {
      display: none;

      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .frisbuy-customer-review__play-icon {
      position: absolute;
      inset: 50%;
      transform: translate(-50%, -50%);

      width: 50%;
      height: 50%;

      z-index: 1;

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>

<script setup lang="ts">
// Modules
import { onMounted, nextTick } from "vue";
import { Property } from "csstype";
// Services
import { MediaCatcher } from "@/common/services/ErrorHandler/ErrorCatcher";
import { DomainSubstitute } from "@/common/services/DomainSubstitute/DomainSubstitute";
// Consts
import widgetTypes from "@/common/consts/widgetTypes";
// Types
import { imageWithPostId } from "@/widgets/MarketplaceReviews/interfaces";

export type MediaListCustomization = {
  wrap?: Property.FlexWrap;
  gap?: Property.Gap;
  mediaWidth?: Property.Width;
  mediaHeight?: Property.Height;
  mediaAspectRatio?: Property.AspectRatio;
};

const props = withDefaults(
  defineProps<
    {
      mediaList: Array<imageWithPostId>;
    } & MediaListCustomization
  >(),
  {
    // Styles
    wrap: "nowrap",
    gap: "5px",
    mediaWidth: "80px",
    mediaHeight: "unset",
    mediaAspectRatio: "1 / 1",
  }
);

const emit = defineEmits<{
  (e: "selectMedia", media: imageWithPostId): void;
}>();

function selectMedia(media: imageWithPostId) {
  emit("selectMedia", media);
}

// Safari fix for video height issue
function fixSafariVideoHeight() {
  if (typeof window !== 'undefined' && /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)) {
    nextTick(() => {
      const mediaItems = document.querySelectorAll('.frisbuy-customer-review__media-list-item');
      mediaItems.forEach((item: Element) => {
        const htmlItem = item as HTMLElement;
        // Force reflow by toggling display
        const originalDisplay = htmlItem.style.display;
        htmlItem.style.display = 'none';
        htmlItem.offsetHeight; // Force reflow
        htmlItem.style.display = originalDisplay || 'flex';
      });
    });
  }
}

function errorImageHandler(e: Event) {
  MediaCatcher(e, 2, widgetTypes.STORY);
}

onMounted(() => {
  fixSafariVideoHeight();
});

const errorImagePlug = "https://www.frisbuy.ru/images/empty_media.png";
</script>
